import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, GitBranch, RefreshCw, Ribbon, Scissors, Tag, Users, Video } from "lucide-react";

import { AdminScreen, Section } from "@/admin/kit";
import { api } from "@/lib/api";
import { Spinner } from "@/components/kit";

const NAV = [
  { key: "products", label: "Products", icon: Tag, href: "/admin/products" },
  { key: "hairstyles", label: "Hairstyles & tutorials", icon: Scissors, href: "/admin/hairstyles" },
  { key: "rules", label: "Recommendation rules", icon: GitBranch, href: "/admin/rules" },
  { key: "videos", label: "Video library", icon: Video, href: "/admin/videos" },
  { key: "content", label: "Tips & articles", icon: BookOpen, href: "/admin/content" },
  { key: "brand", label: "Brand, privacy & legal", icon: Ribbon, href: "/admin/brand" },
  { key: "users", label: "Admins & recent changes", icon: Users, href: "/admin/users" },
];
const STATS = [
  ["app_open", "App opens"], ["scan_started", "Scans started"], ["scan_completed", "Scans completed"], ["scan_failed", "Scans failed"],
  ["manual_profile", "Manual profiles"], ["recommendations_viewed", "Recs viewed"], ["style_view", "Style views"], ["tryon_generated", "Try-ons"],
  ["tryon_failed", "Try-ons failed"], ["share", "Shares"], ["add_to_cart", "Add to cart"], ["checkout_click", "Checkout clicks"], ["save", "Saves"],
];

function Bars({ rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (!rows.length) return <p className="t-caption">No data yet.</p>;
  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      {rows.map((r) => (
        <div key={r.key} className="flex flex-col gap-0.5">
          <div className="flex justify-between"><span className="t-caption">{r.key}</span><span className="t-caption font-body-bold">{r.count}</span></div>
          <div className="h-2 overflow-hidden rounded bg-tert"><div className="h-2 rounded bg-ocean transition-all duration-700" style={{ width: `${(r.count / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.app_open ?? 0));
  if (!daily.length) return <p className="t-caption">No data yet.</p>;
  return (
    <div data-testid="admin-daily-chart" className="flex h-[150px] items-end gap-1 overflow-hidden">
      {daily.map((d) => (
        <div key={d.day} className="flex flex-1 flex-col items-center justify-end gap-0.5" title={`${d.day}: ${d.app_open ?? 0}`}>
          <span className="text-[10px] text-mute">{d.app_open ?? 0}</span>
          <div className="w-[70%] rounded-sm bg-navy" style={{ height: Math.max(2, ((d.app_open ?? 0) / max) * 100) }} />
          <span className="text-[9px] text-mute">{d.day.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminHome() {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    setErr(null);
    api(`/admin/analytics?days=${days}`).then(setA).catch((e) => setErr(e.message));
  }, [days]);
  useEffect(load, [load]);
  const t = a?.totals ?? {};
  const perScan = t.scan_completed ? ((t.add_to_cart ?? 0) / t.scan_completed).toFixed(2) : "—";
  const hi = a ? [["installs", "Installs", a.installs], ["success", "Scan success", a.scan_success_rate == null ? "—" : `${Math.round(a.scan_success_rate * 100)}%`], ["per-scan", "Add-to-cart per scan", perScan]] : [];
  const rng = (on) => `focus-ring flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border px-4 font-body-bold text-xs transition-colors ${on ? "border-navy bg-navy text-white" : "border-[#D1D5DB] text-ink hover:border-ocean"}`;

  return (
    <AdminScreen title="Dashboard" testId="admin-home" fallback="/mystyle">
      <nav aria-label="Admin sections" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {NAV.map((n) => (
          <button key={n.key} type="button" data-testid={`admin-nav-${n.key}`} onClick={() => navigate(n.href)} className="focus-ring flex min-h-[88px] flex-col justify-center gap-2 rounded-[20px] border border-line bg-off p-4 text-left transition-all hover:-translate-y-0.5 hover:border-ocean hover:shadow-card">
            <n.icon className="h-[22px] w-[22px] text-navy" aria-hidden />
            <span className="t-strong">{n.label}</span>
          </button>
        ))}
      </nav>
      <div className="mt-3 flex items-center gap-2">
        <h2 className="t-h2 flex-1">Analytics</h2>
        {[7, 30].map((d) => <button key={d} type="button" data-testid={`admin-analytics-${d}d`} aria-pressed={days === d} onClick={() => setDays(d)} className={rng(days === d)}>{d} days</button>)}
        <button type="button" data-testid="admin-analytics-refresh" aria-label="Refresh analytics" onClick={load} className={rng(false)}><RefreshCw className="h-4 w-4 text-navy" aria-hidden /></button>
      </div>
      <p className="t-caption -mt-3">Anonymous, aggregate only — random install ids, no names, emails, IPs or photos.</p>
      {err ? <p data-testid="admin-analytics-error" role="alert" className="t-body !text-[#EF4444]">{err}</p> : !a ? <Spinner /> : (
        <>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
            {hi.map(([k, l, v]) => (
              <div key={k} data-testid={`admin-stat-${k}`} className="rounded-xl border border-navy bg-navy p-3">
                <p className="t-h2 !text-white">{String(v)}</p><p className="t-caption !text-white">{l}</p>
              </div>
            ))}
            {STATS.map(([k, l]) => (
              <div key={k} data-testid={`admin-stat-${k}`} className="rounded-xl border border-line bg-off p-3">
                <p className="t-h2">{t[k] ?? 0}</p><p className="t-caption">{l}</p>
              </div>
            ))}
          </div>
          <Section title={`App opens per day (last ${a.days} days)`}><DailyChart daily={a.daily} /></Section>
          <div className="grid gap-5 md:grid-cols-2">
            <Section title="Top viewed styles"><Bars testId="admin-top-viewed" rows={a.top_viewed} /></Section>
            <Section title="Top tried-on styles"><Bars testId="admin-top-tryons" rows={a.top_tryons} /></Section>
            <Section title="Add-to-cart by product"><Bars testId="admin-add-to-cart" rows={a.add_to_cart} /></Section>
            <Section title="Face shapes (scans)"><Bars testId="admin-face-shapes" rows={a.face_shapes} /></Section>
            <Section title="Hair patterns (scans)"><Bars testId="admin-hair-patterns" rows={a.hair_patterns} /></Section>
          </div>
        </>
      )}
    </AdminScreen>
  );
}
